import ActionType from '@/shared/enums/ActionType';
import Config from '@/lib/config';

const calculateChange = ({
  changed,
  removed,
  removedRole,
  added,
  addedRole,
}) => {
  let value = changed;
  let adjustedAdded = added;
  let adjustedRemoved = removed;

  if (addedRole < 0) {
    value += addedRole;
  } else {
    adjustedAdded -= addedRole;
  }

  value += adjustedAdded;

  if (removedRole < 0) {
    value += removedRole;
  } else {
    adjustedRemoved -= removedRole;
  }

  value += adjustedRemoved;

  return value;
};

/**
 * Returns impact and difference metrics.
 *
 * @param {Object}
 *
 * @return {Object}
 */
export default function calculateImpactMetrics({
  comparisonMetrics,
  impactMap,
  scenarioMetrics,
}) {
  // Hours.
  const increasedHours = impactMap?.get(ActionType.INCREASED_HOURS);
  const reducedHours = impactMap?.get(ActionType.REDUCED_HOURS);

  // FTE.
  const increasedFTE = impactMap?.get(ActionType.INCREASED_FTE);
  const reducedFTE = impactMap?.get(ActionType.REDUCED_FTE);

  // Budget.
  const increasedBudget = impactMap?.get(ActionType.INCREASED_BUDGET);
  const reducedBudget = impactMap?.get(ActionType.REDUCED_BUDGET);

  // Activities.
  const addedActivities = impactMap?.get(ActionType.ADD_ACTIVITIES);
  const removedActivities = impactMap?.get(ActionType.REMOVE_ACTIVITIES);

  // Roles.
  const addedRoles = impactMap?.get(ActionType.ADD_ROLES);
  const removedRoles = impactMap?.get(ActionType.REMOVE_ROLES);

  const createdHours =
    -1 *
    ((reducedHours?.impact.hours || 0) +
      (removedActivities?.impact.hours || 0));
  const usedHours =
    -1 *
    ((increasedHours?.impact.hours || 0) +
      (addedActivities?.impact.hours || 0));

  // We multiply the values by -1 because capacity is the inverse of the real
  // changes applied. Removing hours increases capacity.
  const capacity = {
    created: {
      budget:
        -1 *
        ((reducedBudget?.impact.budget || 0) +
          (removedRoles?.impact.budget || 0)),
      cost:
        -1 *
        ((reducedHours?.impact.cost || 0) +
          (removedActivities?.impact.cost || 0)),
      hours: createdHours,
      equivalentBudget:
        -1 *
        ((reducedHours?.impact.activityBudget || 0) +
          (removedActivities?.impact.budget || 0)),
      equivalentFTE: createdHours / Config.FTE_HOURS_PER_WEEK,
      fte:
        -1 * ((reducedFTE?.impact.fte || 0) + (removedRoles?.impact.fte || 0)),
      roles: removedRoles?.impact.roles || 0,
    },
    used: {
      budget:
        -1 *
        ((increasedBudget?.impact.budget || 0) +
          (addedRoles?.impact.budget || 0)),
      cost:
        -1 *
        ((increasedHours?.impact.cost || 0) +
          (addedActivities?.impact.cost || 0)),
      equivalentBudget:
        -1 *
        ((increasedHours?.impact.activityBudget || 0) +
          (addedActivities?.impact.budget || 0)),
      equivalentFTE: usedHours / Config.FTE_HOURS_PER_WEEK,
      fte:
        -1 * ((increasedFTE?.impact.fte || 0) + (addedRoles?.impact.fte || 0)),
      hours:
        -1 *
        ((increasedHours?.impact.hours || 0) +
          (addedActivities?.impact.hours || 0)),
      roles: addedRoles?.impact.roles || 0,
    },
  };

  // Calculates the net changes.
  const { created, used } = capacity;
  capacity.net = {
    budget: created.budget + used.budget,
    cost: created.cost + used.cost,
    equivalentBudget: created.equivalentBudget + used.equivalentBudget,
    equivalentFTE: created.equivalentFTE + used.equivalentFTE,
    fte: created.fte + used.fte,
    hours: created.hours + used.hours,
    roles: created.roles + used.roles,
  };

  // Calculates the available capacity. For now it's simply the equivalent
  // changes.
  const { net } = capacity;
  capacity.available = {
    budget: net.equivalentBudget,
    cost: net.cost,
    fte: net.equivalentFTE,
    hours: net.hours,
  };

  const metrics = [
    {
      ...comparisonMetrics,
      activityBudget: comparisonMetrics?.budget,
    },
    {
      ...scenarioMetrics,
      activityBudget: scenarioMetrics.budget - net.equivalentBudget,
    },
  ];

  return {
    capacity,
    difference: [
      {
        budget: comparisonMetrics.budget,
        cost: comparisonMetrics.cost,
        fte: comparisonMetrics.fte,
        hours: comparisonMetrics.hours,
      },
      {
        budget: comparisonMetrics.budget + capacity.available.budget,
        cost: comparisonMetrics.cost + capacity.available.cost,
        fte: comparisonMetrics.fte + capacity.available.fte,
        hours: comparisonMetrics.hours + capacity.available.hours,
      },
    ],
    metrics,
  };
}
